import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { DoctorProfile, fetchProfile } from 'services/api/profile';

// REST replacement for the Apollo cache-only reads of GET_DOCTOR_PROFILE:
// fetch once per DATA locale, share via context, refresh() after writes
// (admin-front-new DoctorProfileContext pattern).
type ContextValue = {
  profile: DoctorProfile | null;
  loading: boolean;
  refresh: () => void;
};

const DoctorProfileContext = createContext<ContextValue | undefined>(undefined);

interface IProps {
  // DATA locale being viewed/edited (the ?lang= URL param), not the UI language
  lang?: string;
  children: ReactNode;
}

export const DoctorProfileProvider: FC<IProps> = ({ lang, children }) => {
  const [profile, setProfile] = useState<DoctorProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    fetchProfile(lang)
      .then(setProfile)
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [lang]);

  useEffect(() => {
    load();
  }, [load]);

  const value = useMemo(
    () => ({ profile, loading, refresh: load }),
    [profile, loading, load]
  );

  return (
    <DoctorProfileContext.Provider value={value}>
      {children}
    </DoctorProfileContext.Provider>
  );
};

export const useDoctorProfile = (): ContextValue => {
  const context = useContext(DoctorProfileContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'useDoctorProfile must be used within a DoctorProfileProvider'
    );
  }

  return context;
};
